// The only place Express talks to Google Analytics. Every event is listed in docs/analytics-events.md.
import ReactGA from "react-ga4"
import { SettingConstant } from "shared/constants/setting-constant"
import { getAccessTokenData } from "utils/token-helper"

const EVENT_NAME = /^[a-z][a-z0-9_]{0,39}$/
const MAX_PARAMS = 25
const MAX_VALUE_LENGTH = 100

// The signed-in user's names, which must never reach GA (set in setUserContext; emails are caught by "@")
let blockedValues = []
let userId = ""
let isInternalUser = false
let pagePath = ""

export function isValidEventName(name) {
  return typeof name === "string" && EVENT_NAME.test(name)
}

// Drops anything GA can't store or must not see: personal data, long text, objects. Numbers and booleans are kept.
export function cleanParams(params = {}) {
  const clean = {}
  for (const [key, raw] of Object.entries(params)) {
    if (Object.keys(clean).length >= MAX_PARAMS) break
    if (raw === undefined || raw === null || raw === "") continue
    if (typeof raw === "number" || typeof raw === "boolean") {
      clean[key] = raw
      continue
    }
    if (typeof raw !== "string") continue
    if (raw.includes("@") || blockedValues.some(b => b.test(raw))) {
      console.warn(`analytics: dropped param "${key}" (looks like personal data)`)
      continue
    }
    clean[key] = raw.slice(0, MAX_VALUE_LENGTH)
  }
  return clean
}

// Short, non-personal code for a failed API result (never the message itself, which can hold order numbers)
export function errorType(result) {
  if (!result) return "no_response"
  const status = result.statusCode ?? result.STATUS_CODE ?? result.status ?? result.error?.statusCode
  if (/^\d+$/.test(String(status ?? ""))) return `status_${status}`
  return typeof result === "string" ? "message" : "rejected"
}

// List pages name their export file Express_<Entity> (Express_SalesOrder, Express_DG_Item_Profile...)
export function entityFromExportName(fileName = "") {
  const entity = fileName
    .replace(/^Express_/, "")
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .toLowerCase()
  return entity === "inventory" ? "product" : entity
}

export function initAnalytics() {
  const debug = process.env.NODE_ENV === "development" || window.location.search.includes("debug_mode=true")
  ReactGA.initialize(SettingConstant.GA_MEASUREMENT_ID || "G-PBT96QFNZ9", {
    // Page views are sent per route by trackPageView, not by gtag on load
    gtagOptions: { send_page_view: false, ...(debug && { debug_mode: true }) },
  })
}

export function trackEvent(name, params = {}) {
  if (!isValidEventName(name)) {
    console.warn(`analytics: invalid event name "${name}"`)
    return
  }
  ReactGA.event(name, cleanParams({ page_path: pagePath, ...params }))
}

// path is the route template (/sales-order/:erpOrder/:id), never the concrete URL
export function trackPageView(path, title) {
  pagePath = path
  document.title = `${title} · Express`
  ReactGA.event("page_view", {
    page_path: path,
    page_title: title,
    page_location: `${window.location.origin}/#${path}`,
  })
}

export function setUserContext({ companyCode, isInternal }) {
  const token = getAccessTokenData() || {}
  userId = token.oid || token.sub || ""
  isInternalUser = !!isInternal
  // Whole words only, so a user called "Ana" doesn't block "analytics_outbound"
  blockedValues = [token.name, token.given_name, token.family_name]
    .filter(v => v && v.trim().length > 1)
    .map(v => new RegExp(`(^|[^a-z0-9])${v.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}($|[^a-z0-9])`, "i"))

  ReactGA.set({
    user_id: userId || undefined,
    user_properties: {
      client_code: companyCode || "",
      user_type: isInternalUser ? "internal" : "client",
      app_version: SettingConstant.APP_VERSION,
    },
  })
}

// For the eStore event log, so its records can be joined to GA
export function getAnalyticsContext() {
  return { userId, isInternal: isInternalUser, appVersion: SettingConstant.APP_VERSION, route: pagePath }
}
